import { CheckCircle, FileText, Trash2, Upload, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

type IngestionJob = {
	id: string;
	filename: string;
	source: string;
	status: 'completed' | 'failed' | 'processing';
	records: number;
	uploaded_by: string;
	uploaded_at: string;
};

const MOCK_JOBS: IngestionJob[] = [
	{
		id: 'job_1',
		filename: 'ofac_sdn_list_2025.csv',
		source: 'OFAC',
		status: 'completed',
		records: 12450,
		uploaded_by: 'Alex Morgan',
		uploaded_at: new Date(Date.now() - 86400000 * 2).toISOString(),
	},
	{
		id: 'job_2',
		filename: 'un_sanctions_q2_2025.xml',
		source: 'UN Sanctions',
		status: 'completed',
		records: 3821,
		uploaded_by: 'Jordan Kim',
		uploaded_at: new Date(Date.now() - 86400000 * 5).toISOString(),
	},
	{
		id: 'job_3',
		filename: 'eu_consolidated_list.csv',
		source: 'EU Sanctions',
		status: 'failed',
		records: 0,
		uploaded_by: 'Sam Rivera',
		uploaded_at: new Date(Date.now() - 86400000 * 7).toISOString(),
	},
	{
		id: 'job_4',
		filename: 'pep_database_may2025.csv',
		source: 'World-Check',
		status: 'completed',
		records: 54000,
		uploaded_by: 'Taylor Brooks',
		uploaded_at: new Date(Date.now() - 86400000 * 10).toISOString(),
	},
];

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

export default function WatchlistIngestionPage() {
	const [source, setSource] = useState('');
	const [format, setFormat] = useState('');
	const [dragging, setDragging] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [jobs, setJobs] = useState<IngestionJob[]>(MOCK_JOBS);

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) setSelectedFile(file);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) setSelectedFile(file);
	};

	const handleUpload = () => {
		if (!selectedFile || !source) {
			toast.error('Select a source and file');
			return;
		}
		setUploading(true);
		setUploadProgress(0);

		const interval = setInterval(() => {
			setUploadProgress((prev) => {
				if (prev >= 100) {
					clearInterval(interval);
					setUploading(false);
					setSelectedFile(null);
					setSource('');
					setFormat('');
					setUploadProgress(0);
					setJobs((prev) => [
						{
							id: `job_${Date.now()}`,
							filename: selectedFile.name,
							source,
							status: 'completed',
							records: Math.floor(Math.random() * 10000) + 500,
							uploaded_by: 'Alex Morgan',
							uploaded_at: new Date().toISOString(),
						},
						...prev,
					]);
					toast.success('Watchlist ingested successfully');
					return 100;
				}
				return prev + 20;
			});
		}, 300);
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Watchlist Ingestion
				</h1>
				<p className="text-sm text-[#737373] mt-0.5">
					Upload and manage watchlist data sources
				</p>
			</div>

			<div className="p-6 space-y-6 max-w-[900px]">
				{/* Upload Form */}
				<Card className="border border-[#E5E5E5]">
					<CardHeader className="pb-3">
						<CardTitle className="text-base font-semibold text-[#0A0A0A]">
							Upload Watchlist File
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-1.5">
								<Label>
									Source <span className="text-red-500">*</span>
								</Label>
								<Select value={source} onValueChange={setSource}>
									<SelectTrigger>
										<SelectValue placeholder="Select source" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="OFAC">OFAC</SelectItem>
										<SelectItem value="UN Sanctions">UN Sanctions</SelectItem>
										<SelectItem value="EU Sanctions">EU Sanctions</SelectItem>
										<SelectItem value="Interpol">Interpol</SelectItem>
										<SelectItem value="World-Check">World-Check</SelectItem>
										<SelectItem value="Dow Jones">Dow Jones</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1.5">
								<Label>File Format</Label>
								<Select value={format} onValueChange={setFormat}>
									<SelectTrigger>
										<SelectValue placeholder="Auto-detect" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="csv">CSV</SelectItem>
										<SelectItem value="xml">XML</SelectItem>
										<SelectItem value="json">JSON</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>

						{/* Drop zone */}
						<div
							role="region"
							aria-label="File drop zone"
							onDragOver={(e) => {
								e.preventDefault();
								setDragging(true);
							}}
							onDragLeave={() => setDragging(false)}
							onDrop={handleDrop}
							className={cn(
								'rounded-xl border-2 border-dashed p-8 text-center transition-colors',
								dragging
									? 'border-[#0070C9] bg-blue-50'
									: 'border-[#D4D4D4] hover:border-[#A3A3A3]',
								selectedFile ? 'border-green-400 bg-green-50' : '',
							)}
						>
							{selectedFile ? (
								<div className="flex flex-col items-center gap-2">
									<FileText className="h-8 w-8 text-green-600" />
									<div className="text-sm font-medium text-[#0A0A0A]">
										{selectedFile.name}
									</div>
									<div className="text-xs text-[#737373]">
										{(selectedFile.size / 1024).toFixed(1)} KB
									</div>
									<Button
										variant="ghost"
										size="sm"
										className="text-red-600 hover:text-red-700 gap-1"
										onClick={() => setSelectedFile(null)}
									>
										<Trash2 className="h-3.5 w-3.5" /> Remove
									</Button>
								</div>
							) : (
								<div className="flex flex-col items-center gap-2">
									<Upload className="h-8 w-8 text-[#A3A3A3]" />
									<div className="text-sm font-medium text-[#525252]">
										Drag & drop a file, or{' '}
										<label className="cursor-pointer text-[#0070C9] hover:underline">
											browse
											<input
												type="file"
												className="sr-only"
												accept=".csv,.xml,.json"
												onChange={handleFileChange}
											/>
										</label>
									</div>
									<div className="text-xs text-[#A3A3A3]">
										CSV, XML, or JSON — max 50 MB
									</div>
								</div>
							)}
						</div>

						{uploading && (
							<div className="space-y-2">
								<div className="flex justify-between text-xs text-[#737373]">
									<span>Uploading and processing...</span>
									<span>{uploadProgress}%</span>
								</div>
								<Progress value={uploadProgress} className="h-2" />
							</div>
						)}

						<Button
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90 gap-2"
							disabled={!selectedFile || !source || uploading}
							onClick={handleUpload}
						>
							<Upload className="h-4 w-4" />
							{uploading ? 'Processing...' : 'Upload & Ingest'}
						</Button>
					</CardContent>
				</Card>

				{/* Ingestion History */}
				<div>
					<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
						Recent Ingestions
					</div>
					<Card className="border border-[#E5E5E5] overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-[#FAFAFA]">
									<TableHead className="text-xs font-semibold text-[#737373]">
										Filename
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Source
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Status
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Records
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Uploaded By
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Date
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{jobs.map((job) => (
									<TableRow key={job.id}>
										<TableCell>
											<div className="flex items-center gap-1.5 text-sm text-[#0A0A0A]">
												<FileText className="h-4 w-4 shrink-0 text-[#737373]" />
												<span className="truncate max-w-[200px]">
													{job.filename}
												</span>
											</div>
										</TableCell>
										<TableCell>
											<span className="text-sm text-[#525252]">
												{job.source}
											</span>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-1.5">
												{job.status === 'completed' && (
													<CheckCircle className="h-4 w-4 text-green-600" />
												)}
												{job.status === 'failed' && (
													<XCircle className="h-4 w-4 text-red-600" />
												)}
												<Badge
													variant={
														job.status === 'completed'
															? 'secondary'
															: job.status === 'failed'
																? 'destructive'
																: 'outline'
													}
													className="text-xs capitalize"
												>
													{job.status}
												</Badge>
											</div>
										</TableCell>
										<TableCell>
											<span className="text-sm text-[#525252]">
												{job.records > 0 ? job.records.toLocaleString() : '—'}
											</span>
										</TableCell>
										<TableCell>
											<span className="text-sm text-[#525252]">
												{job.uploaded_by}
											</span>
										</TableCell>
										<TableCell>
											<span className="text-xs text-[#737373]">
												{formatDate(job.uploaded_at)}
											</span>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Card>
				</div>
			</div>
		</div>
	);
}
